<script lang="ts">
	import { locale } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { tagManager, type TagInfo } from '$lib/docs/tags';
	import type { Language } from '$lib/docs/types';
	
	const currentLocale = $derived($locale as Language || 'en');
	
	let allTags = $state<TagInfo[]>([]);
	let groupedTags = $state<Map<string, TagInfo[]>>(new Map());
	let loading = $state(true);
	let searchQuery = $state('');
	let selectedCategory = $state<string>('all');
	
	const categories = {
		'all': 'All Tags',
		'getting-started': 'Getting Started',
		'features': 'Features',
		'developer': 'Developer',
		'business': 'Business',
		'security': 'Security',
		'support': 'Support'
	};
	
	const categoryMapping: Record<string, string[]> = {
		'getting-started': ['introduction', 'overview', 'getting-started', 'installation', 'quick-start', 'setup'],
		'features': ['projects', 'tasks', 'time-tracking', 'teams', 'collaboration', 'workspaces', 'organization', 'calendar', 'reports'],
		'developer': ['api', 'cli', 'webhooks', 'integrations', 'command-line', 'terminal', 'developers'],
		'business': ['billing', 'invoicing', 'clients', 'customer-management', 'enterprise'],
		'security': ['authentication', 'security', 'privacy', 'permissions', 'sso'],
		'support': ['troubleshooting', 'faq', 'contact', 'support', 'help']
	};
	
	onMount(async () => {
		await loadTags();
	});
	
	async function loadTags() {
		loading = true;
		try {
			const collection = await tagManager.getTagCollection(currentLocale);
			if (collection) {
				allTags = Array.from(collection.all.values()).sort((a, b) => b.count - a.count);
				groupTags();
			}
		} catch (error) {
			console.error('Error loading tags:', error);
		} finally {
			loading = false;
		}
	}
	
	function groupTags() {
		const groups = new Map<string, TagInfo[]>();
		
		// Group by first letter
		allTags.forEach(tag => {
			const firstLetter = tag.name[0].toUpperCase();
			if (!groups.has(firstLetter)) {
				groups.set(firstLetter, []);
			}
			groups.get(firstLetter)!.push(tag);
		});
		
		// Sort groups alphabetically
		const sortedGroups = new Map(
			Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
		);
		
		groupedTags = sortedGroups;
	}
	
	const filteredTags = $derived.by(() => {
		let tags = allTags;
		
		// Filter by category
		if (selectedCategory !== 'all') {
			const categoryTags = categoryMapping[selectedCategory] || [];
			tags = tags.filter(tag => categoryTags.includes(tag.name));
		}
		
		// Filter by search query
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			tags = tags.filter(tag => 
				tag.name.toLowerCase().includes(query)
			);
		}
		
		return tags;
	});
	
	const tagStats = $derived.by(() => {
		const totalArticles = allTags.reduce((sum, tag) => sum + tag.count, 0);
		const avgArticlesPerTag = allTags.length > 0 ? Math.round(totalArticles / allTags.length) : 0;
		return {
			totalTags: allTags.length,
			totalArticles,
			avgArticlesPerTag
		};
	});
</script>

<svelte:head>
	<title>All Tags - Documentation - Heyweek</title>
	<meta name="description" content="Browse all Heyweek documentation tags to find guides, references, and how-tos by topic — time tracking, invoicing, the CLI, integrations, and team setup." />
	<meta property="og:title" content="All Tags - Documentation - Heyweek" />
	<meta property="og:description" content="Browse all Heyweek documentation tags to find guides, references, and how-tos by topic — time tracking, invoicing, the CLI, integrations, and team setup." />
</svelte:head>

<div class="tags-index">
	<div class="tags-index__container">
		<!-- Header -->
		<header class="tags-index__header">
			<div class="tags-index__breadcrumb">
				<a href="/docs" class="tags-index__breadcrumb-link">Documentation</a>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
					<path d="M5.5 4L9.5 8L5.5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
				</svg>
				<span class="tags-index__breadcrumb-current">Tags</span>
			</div>
			
			<h1 class="tags-index__title">Documentation Tags</h1>
			<p class="tags-index__description">
				Browse articles by topic to quickly find the information you need
			</p>
			
			{#if !loading}
				<div class="tags-index__stats">
					<div class="tags-index__stat">
						<span class="tags-index__stat-value">{tagStats.totalTags}</span>
						<span class="tags-index__stat-label">Tags</span>
					</div>
					<div class="tags-index__stat">
						<span class="tags-index__stat-value">{tagStats.totalArticles}</span>
						<span class="tags-index__stat-label">Tagged Articles</span>
					</div>
					<div class="tags-index__stat">
						<span class="tags-index__stat-value">{tagStats.avgArticlesPerTag}</span>
						<span class="tags-index__stat-label">Avg per Tag</span>
					</div>
				</div>
			{/if}
		</header>
		
		<!-- Filters -->
		<div class="tags-index__filters">
			<div class="tags-index__search">
				<svg class="tags-index__search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
					<path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
				</svg>
				<input
					type="text"
					bind:value={searchQuery}
					placeholder="Search tags..."
					class="tags-index__search-input"
				/>
			</div>
			
			<div class="tags-index__categories">
				{#each Object.entries(categories) as [key, label]}
					<button
						class="tags-index__category"
						class:tags-index__category--active={selectedCategory === key}
						onclick={() => selectedCategory = key}
					>
						{label}
					</button>
				{/each}
			</div>
		</div>
		
		<!-- Tags List -->
		{#if loading}
			<div class="tags-index__loading">
				<div class="tags-index__spinner"></div>
				<p>Loading tags...</p>
			</div>
		{:else if filteredTags.length === 0}
			<div class="tags-index__empty">
				<svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor" opacity="0.2">
					<path d="M38.86 36.74L30.12 28a13.5 13.5 0 10-2.12 2.12l8.74 8.74a1.5 1.5 0 002.12-2.12zM6 19.5a13.5 13.5 0 1127 0 13.5 13.5 0 01-27 0z"/>
				</svg>
				<p>No tags found matching your criteria</p>
				<button
					class="tags-index__reset"
					onclick={() => {
						searchQuery = '';
						selectedCategory = 'all';
					}}
				>
					Reset filters
				</button>
			</div>
		{:else}
			<div class="tags-index__grid">
				{#each filteredTags as tag}
					<a
						href="/docs/tags/{encodeURIComponent(tag.name)}"
						class="tag-card"
					>
						<div class="tag-card__header">
							<span class="tag-card__name">#{tag.name}</span>
							<span class="tag-card__count">{tag.count}</span>
						</div>
						<div class="tag-card__articles">
							{#each tag.articles.slice(0, 3) as article}
								<div class="tag-card__article">{article.title}</div>
							{/each}
							{#if tag.articles.length > 3}
								<div class="tag-card__more">
									+{tag.articles.length - 3} more
								</div>
							{/if}
						</div>
					</a>
				{/each}
			</div>
		{/if}
	</div>
</div>

<style>
	.tags-index {
		min-height: 100vh;
		padding: 2rem 1.5rem;
	}
	
	.tags-index__container {
		max-width: 1200px;
		margin: 0 auto;
	}
	
	/* Breadcrumb */
	.tags-index__breadcrumb {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 2rem;
		font-size: 0.875rem;
	}
	
	.tags-index__breadcrumb-link {
		color: var(--text-tertiary);
		text-decoration: none;
		transition: color 0.2s ease;
	}
	
	.tags-index__breadcrumb-link:hover {
		color: var(--text-primary);
	}
	
	.tags-index__breadcrumb-current {
		color: var(--text-primary);
		font-weight: 500;
	}
	
	.tags-index__breadcrumb svg {
		color: var(--text-tertiary);
		width: 12px;
		height: 12px;
	}
	
	/* Header */
	.tags-index__header {
		margin-bottom: 2rem;
	}
	
	.tags-index__title {
		font-size: 2rem;
		font-weight: 600;
		color: var(--text-primary);
		margin: 0 0 0.5rem 0;
		line-height: 1.3;
	}
	
	.tags-index__description {
		font-size: 1rem;
		color: var(--text-secondary);
		margin: 0 0 1.5rem 0;
		line-height: 1.5;
	}
	
	/* Stats */
	.tags-index__stats {
		display: flex;
		gap: 2rem;
		padding: 1rem 0;
		border-top: 1px solid var(--border-light);
		border-bottom: 1px solid var(--border-light);
	}
	
	.tags-index__stat {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}
	
	.tags-index__stat-value {
		font-size: 1.5rem;
		font-weight: 600;
		color: var(--text-primary);
	}
	
	.tags-index__stat-label {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}
	
	/* Filters */
	.tags-index__filters {
		margin-bottom: 2rem;
	}
	
	.tags-index__search {
		position: relative;
		margin-bottom: 1rem;
	}
	
	.tags-index__search-icon {
		position: absolute;
		left: 1rem;
		top: 50%;
		transform: translateY(-50%);
		color: var(--text-tertiary);
	}
	
	.tags-index__search-input {
		width: 100%;
		padding: 0.75rem 1rem 0.75rem 2.75rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 8px;
		font-size: 0.875rem;
		color: var(--text-primary);
		transition: all 0.2s ease;
	}
	
	.tags-index__search-input:focus {
		outline: none;
		border-color: var(--brand-primary);
		background: var(--bg-primary);
	}
	
	.tags-index__search-input::placeholder {
		color: var(--text-tertiary);
	}
	
	.tags-index__categories {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	
	.tags-index__category {
		padding: 0.5rem 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 20px;
		font-size: 0.875rem;
		color: var(--text-secondary);
		cursor: pointer;
		transition: all 0.2s ease;
	}
	
	.tags-index__category:hover {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
		color: var(--text-primary);
	}
	
	.tags-index__category--active {
		background: var(--brand-muted);
		border-color: var(--brand-primary);
		color: var(--brand-primary);
	}
	
	/* Loading */
	.tags-index__loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		text-align: center;
	}
	
	.tags-index__spinner {
		width: 32px;
		height: 32px;
		border: 3px solid var(--border-light);
		border-top-color: var(--brand-primary);
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
		margin-bottom: 1rem;
	}
	
	@keyframes spin {
		to { transform: rotate(360deg); }
	}
	
	/* Empty State */
	.tags-index__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		text-align: center;
		color: var(--text-tertiary);
	}
	
	.tags-index__empty p {
		margin: 1rem 0;
		color: var(--text-secondary);
	}
	
	.tags-index__reset {
		padding: 0.5rem 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 6px;
		color: var(--text-primary);
		cursor: pointer;
		transition: all 0.2s ease;
	}
	
	.tags-index__reset:hover {
		background: var(--bg-tertiary);
		border-color: var(--brand-primary);
		color: var(--brand-primary);
	}
	
	/* Grid */
	.tags-index__grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
		gap: 1rem;
	}
	
	.tag-card {
		display: flex;
		flex-direction: column;
		padding: 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 8px;
		text-decoration: none;
		transition: all 0.2s ease;
	}
	
	.tag-card:hover {
		border-color: var(--brand-primary);
		background: var(--bg-tertiary);
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	}
	
	.tag-card__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.75rem;
	}
	
	.tag-card__name {
		font-size: 1rem;
		font-weight: 600;
		color: var(--text-primary);
	}
	
	.tag-card:hover .tag-card__name {
		color: var(--brand-primary);
	}
	
	.tag-card__count {
		padding: 0.25rem 0.5rem;
		background: var(--bg-primary);
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--text-tertiary);
	}
	
	.tag-card:hover .tag-card__count {
		background: var(--brand-muted);
		color: var(--brand-primary);
	}
	
	.tag-card__articles {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}
	
	.tag-card__article {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	
	.tag-card__more {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		font-style: italic;
		margin-top: 0.25rem;
	}
	
	/* Dark mode */
	:global([data-theme='dark']) .tags-index__search-input {
		background: rgba(255, 255, 255, 0.04);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .tags-index__search-input:focus {
		background: rgba(255, 255, 255, 0.06);
		border-color: #ff5e7e;
	}
	
	:global([data-theme='dark']) .tags-index__category {
		background: rgba(255, 255, 255, 0.04);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .tags-index__category:hover {
		background: rgba(255, 255, 255, 0.06);
		border-color: rgba(255, 255, 255, 0.12);
	}
	
	:global([data-theme='dark']) .tags-index__category--active {
		background: rgba(255, 94, 126, 0.15);
		border-color: #ff5e7e;
		color: #ff7a99;
	}
	
	:global([data-theme='dark']) .tag-card {
		background: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .tag-card:hover {
		background: rgba(255, 255, 255, 0.04);
		border-color: #ff5e7e;
	}
	
	:global([data-theme='dark']) .tag-card__count {
		background: rgba(255, 255, 255, 0.06);
	}
	
	:global([data-theme='dark']) .tag-card:hover .tag-card__count {
		background: rgba(255, 94, 126, 0.15);
		color: #ff7a99;
	}
	
	/* Mobile */
	@media (max-width: 768px) {
		.tags-index {
			padding: 1.5rem 1rem;
		}
		
		.tags-index__title {
			font-size: 1.5rem;
		}
		
		.tags-index__stats {
			gap: 1rem;
		}
		
		.tags-index__grid {
			grid-template-columns: 1fr;
		}
	}
</style>